import { updatePreferences } from '@/analytics/consent-hub/consent-client';

import type { FormConsentMode } from './uniform-form-schema';
import type { FormValues } from './uniform-form-validation';

const SUBMISSION_TIMEOUT_MS = 15_000;

const withTimeout = async <T>(promise: Promise<T>): Promise<T> => {
	let timeoutId: ReturnType<typeof setTimeout> | undefined;
	const timeoutPromise = new Promise<never>((_, reject) => {
		timeoutId = setTimeout(
			() => reject(new UniformFormSubmissionError('timeout')),
			SUBMISSION_TIMEOUT_MS,
		);
	});
	try {
		return await Promise.race([promise, timeoutPromise]);
	} finally {
		if (timeoutId) clearTimeout(timeoutId);
	}
};

export type FormFailureCategory = 'preferences' | 'lead' | 'timeout';

export class UniformFormSubmissionError extends Error {
	public readonly category: FormFailureCategory;

	public constructor(category: FormFailureCategory) {
		super(category);
		this.name = 'UniformFormSubmissionError';
		this.category = category;
	}
}

export type SubmitUniformFormInput = {
	client: Parameters<typeof updatePreferences>[0];
	consentCheckboxValue: boolean;
	consentMode: FormConsentMode;
	displayedLegalText: string;
	formId: string;
	formType: string;
	formValues: FormValues;
	intercomAppId?: string;
	intercomTracking: boolean;
	locale: string;
};

const getCookie = (name: string): string => {
	const match = document.cookie
		.split('; ')
		.find((item) => item.startsWith(`${name}=`))
		?.slice(name.length + 1);
	return match ? decodeURIComponent(match).replace(/"/g, '') : '';
};

const getAttribution = (): Record<string, string> => {
	const params = new URLSearchParams(window.location.search);
	return {
		utm_source: params.get('utm_source') ?? '',
		utm_medium: params.get('utm_medium') ?? '',
		utm_campaign: params.get('utm_campaign') ?? '',
		utm_content: params.get('utm_content') ?? '',
		utm_term: params.get('utm_term') ?? '',
	};
};

const getConsentTexts = (
	consentMode: FormConsentMode,
	displayedLegalText: string,
): Pick<
	Parameters<typeof updatePreferences>[1],
	'consentLabelText' | 'privacyPolicyText' | 'ndaText'
> => {
	switch (consentMode) {
		case 'generalMarketingOptIn':
			return { consentLabelText: displayedLegalText };
		case 'privacyPolicy':
			return { privacyPolicyText: displayedLegalText };
		case 'termsOfService':
		case 'eapTermsOfService':
		case 'eapVirtualAgentTermsAndConditions':
			return { ndaText: displayedLegalText };
		case 'none':
			return {};
	}
};

export const submitUniformForm = async ({
	client,
	consentCheckboxValue,
	consentMode,
	displayedLegalText,
	formId,
	formType,
	formValues,
	intercomAppId,
	intercomTracking,
	locale,
}: SubmitUniformFormInput): Promise<void> => {
	const email = String(formValues['email'] ?? '');
	const controller = new AbortController();
	const timeout = window.setTimeout(() => controller.abort(), SUBMISSION_TIMEOUT_MS);

	try {
		try {
			const responseStatus = await withTimeout(
				updatePreferences(client, {
					email,
					formId,
					formUrl: window.location.href,
					paramsLocale: locale,
					site: 'atlassian',
					source: 'uniform',
					consentCheckboxValue,
					...getConsentTexts(consentMode, displayedLegalText),
				}),
			);
			if (typeof responseStatus === 'number' && (responseStatus < 200 || responseStatus >= 300)) {
				throw new Error(`Consent preferences update failed with status ${responseStatus}`);
			}
		} catch (error) {
			if (error instanceof UniformFormSubmissionError) throw error;
			throw new UniformFormSubmissionError('preferences');
		}

		const now = new Date();
		let response: Response;
		try {
			response = await fetch('/endpoint/streamhub/wacformlead', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				signal: controller.signal,
				body: JSON.stringify({
					...formValues,
					...getAttribution(),
					formType,
					formDef: 'pointALead',
					formSubmissionId: crypto.randomUUID(),
					created_at: now.toISOString(),
					submitted: now.toString(),
					source_sequence: now.getTime(),
					locale,
					formUrl: window.location.href,
					referrer: document.referrer,
					userAgent: window.navigator.userAgent,
					marketoCookieId: getCookie('_mkto_trk'),
					anonymousId: getCookie('ajs_anonymous_id'),
					atlassianId: getCookie('ajs_user_id'),
					isIntercomTracking: intercomTracking,
					intercomAppId: intercomTracking ? (intercomAppId ?? '') : '',
				}),
			});
		} catch {
			if (controller.signal.aborted) throw new UniformFormSubmissionError('timeout');
			throw new UniformFormSubmissionError('lead');
		}
		if (!response.ok) throw new UniformFormSubmissionError('lead');
	} finally {
		window.clearTimeout(timeout);
	}
};
